import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDealContext } from "../hooks/useDealContext";
import { updateDealSettings, fetchDeal, autoProject } from "../api/client";
import { useToast } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";
import { fmtSF, fmtPercent } from "../utils/format";
import { Settings, Save } from "lucide-react";
import LocationPanel from "../components/property/LocationPanel";
import NearbyTenantsPanel from "../components/property/NearbyTenantsPanel";
import BOVWriteupPanel from "../components/property/BOVWriteupPanel";
import type { TenantSuggestion } from "../api/client";

const ASSET_CLASSES = ["Office", "Retail", "Industrial", "Multifamily", "Mixed-Use", "Other"];

/** Normalize a stored asset class string (possibly lowercase) to the display value. */
function normalizeAssetClass(raw: string | null | undefined): string {
  if (!raw) return "Retail";
  const lower = raw.toLowerCase();
  return ASSET_CLASSES.find(ac => ac.toLowerCase() === lower) || "Retail";
}

export default function PropertySettingsPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal } = useDealContext();
  const toast = useToast();
  const prop = deal.property_data;

  const [form, setForm] = useState({
    name: deal.name || "",
    address: deal.address || "",
    asset_class: normalizeAssetClass(deal.asset_class || prop?.asset_class),
    total_leasable_area: String(prop?.total_leasable_area || ""),
    analysis_start_date: prop?.analysis_start_date || "",
    num_buildings: String(prop?.num_buildings || ""),
    year_built: String(prop?.year_built || ""),
    parking_spaces: String(prop?.parking_spaces || ""),
    notes: prop?.notes || "",
    tax_reassessment_enabled: Boolean(prop?.tax_reassessment_enabled),
    tax_reassessment_pct_of_price: String(
      prop?.tax_reassessment_pct_of_price != null
        ? (prop.tax_reassessment_pct_of_price as number) * 100
        : "100"
    ),
    tax_reassessment_millage: String(
      prop?.tax_reassessment_millage != null
        ? (prop.tax_reassessment_millage as number) * 100
        : ""
    ),
  });
  const [saving, setSaving] = useState(false);

  // GLA warning
  const tenantSF = prop?.tenants
    ? (prop.tenants as Array<{ leased_sf?: number; square_footage?: number }>).reduce(
        (s, t) => s + (t.leased_sf || t.square_footage || 0), 0
      )
    : 0;
  const glaNum = parseFloat(form.total_leasable_area) || 0;
  const glaDiff = tenantSF > 0 && glaNum > 0 ? Math.abs(glaNum - tenantSF) / tenantSF : 0;

  const handleSave = async () => {
    if (!dealId) return;
    setSaving(true);
    try {
      const propUpdates: Record<string, unknown> = {};
      if (form.total_leasable_area) propUpdates.total_leasable_area = parseFloat(form.total_leasable_area);
      if (form.analysis_start_date) propUpdates.analysis_start_date = form.analysis_start_date;
      if (form.num_buildings) propUpdates.num_buildings = parseInt(form.num_buildings);
      if (form.year_built) propUpdates.year_built = parseInt(form.year_built);
      if (form.parking_spaces) propUpdates.parking_spaces = parseInt(form.parking_spaces);
      if (form.notes) propUpdates.notes = form.notes;
      propUpdates.tax_reassessment_enabled = form.tax_reassessment_enabled;
      if (form.tax_reassessment_pct_of_price) {
        propUpdates.tax_reassessment_pct_of_price = parseFloat(form.tax_reassessment_pct_of_price) / 100;
      }
      propUpdates.tax_reassessment_millage = form.tax_reassessment_millage
        ? parseFloat(form.tax_reassessment_millage) / 100
        : null;

      await updateDealSettings(dealId, {
        name: form.name,
        address: form.address,
        asset_class: form.asset_class,
        property_data: Object.keys(propUpdates).length > 0 ? propUpdates : undefined,
      });

      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      toast.success("Settings saved");

      // Re-run the projection so dashboards reflect the new settings
      // (GLA, analysis start date, tax reassessment, etc.). Non-fatal:
      // the save itself already succeeded.
      try {
        await autoProject(dealId);
        const reprojected = await fetchDeal(dealId);
        setDeal(reprojected);
        toast.success("Projection updated");
      } catch (projErr: any) {
        toast.error(
          `Settings saved, but the projection could not be updated: ${friendlyError(projErr?.response?.data?.detail || projErr?.message || "Try recalculating from the Pro Forma tab.")}`,
        );
      }
    } catch (e: any) {
      toast.error(friendlyError(e?.response?.data?.detail || "Couldn't save the property. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h2 className="font-display text-xl font-semibold text-bark-800 flex items-center gap-2">
          <Settings className="w-5 h-5" /> Property Settings
        </h2>
        <p className="text-sm text-bark-400 mt-1">Edit property-level metadata and configuration</p>
      </div>

      <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-6 space-y-5">
        {/* Property Name */}
        <div>
          <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Property Name</label>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400" />
        </div>

        {/* Address */}
        <div>
          <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Address</label>
          <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400" />
        </div>

        {/* Asset Class + GLA */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Asset Class</label>
            <select value={form.asset_class} onChange={e => setForm(p => ({ ...p, asset_class: e.target.value }))} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400">
              {ASSET_CLASSES.map(ac => <option key={ac} value={ac}>{ac}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Total Leasable Area (SF)</label>
            <input value={form.total_leasable_area} onChange={e => setForm(p => ({ ...p, total_leasable_area: e.target.value }))} type="number" className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums" />
          </div>
        </div>

        {/* GLA Warning */}
        {glaDiff > 0.05 && (
          <div className="bg-honey-50 border border-honey-200 rounded-lg px-4 py-2 text-sm text-honey-800">
            GLA ({fmtSF(glaNum)}) differs from rent roll total ({fmtSF(tenantSF)}) by {fmtPercent(glaDiff, 1)}. This may affect reimbursement calculations.
          </div>
        )}

        {/* Tax Reassessment */}
        <div className="border-t border-grove-200 pt-5">
          <label className="flex items-center gap-2 text-sm font-medium text-bark-700">
            <input
              type="checkbox"
              checked={form.tax_reassessment_enabled}
              onChange={e => setForm(p => ({ ...p, tax_reassessment_enabled: e.target.checked }))}
              className="rounded border-grove-300"
            />
            Reassess property taxes at acquisition
          </label>
          <p className="text-xs text-bark-400 mt-1 ml-6">
            Replaces the historical tax line with assessed value × millage rate, grown from Year 1.
            Use in states that reassess on sale (CA, FL, etc.).
          </p>
          {form.tax_reassessment_enabled && (
            <div className="grid grid-cols-2 gap-4 mt-3 ml-6">
              <div>
                <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Assessed % of Price</label>
                <input
                  value={form.tax_reassessment_pct_of_price}
                  onChange={e => setForm(p => ({ ...p, tax_reassessment_pct_of_price: e.target.value }))}
                  type="number" step="1" placeholder="100"
                  className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Millage / Tax Rate (%)</label>
                <input
                  value={form.tax_reassessment_millage}
                  onChange={e => setForm(p => ({ ...p, tax_reassessment_millage: e.target.value }))}
                  type="number" step="0.05" placeholder="e.g. 1.85"
                  className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums"
                />
              </div>
            </div>
          )}
        </div>

        {/* Analysis Start */}
        <div>
          <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Analysis Start Date</label>
          <input value={form.analysis_start_date} onChange={e => setForm(p => ({ ...p, analysis_start_date: e.target.value }))} type="date" className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400" />
        </div>

        {/* Optional fields */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Num. Buildings</label>
            <input value={form.num_buildings} onChange={e => setForm(p => ({ ...p, num_buildings: e.target.value }))} type="number" className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Year Built</label>
            <input value={form.year_built} onChange={e => setForm(p => ({ ...p, year_built: e.target.value }))} type="number" className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Parking Spaces</label>
            <input value={form.parking_spaces} onChange={e => setForm(p => ({ ...p, parking_spaces: e.target.value }))} type="number" className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 tabular-nums" />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full px-3 py-2 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-400 resize-none" />
        </div>

        {/* Save button */}
        <div className="flex justify-end pt-2">
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-all">
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>

      {dealId && <LocationPanel dealId={dealId} address={form.address} />}
      {dealId && (
        <NearbyTenantsPanel
          dealId={dealId}
          hasLocation={Boolean(prop?.location)}
          initialTenants={(prop?.nearby_tenants as TenantSuggestion[] | undefined) || []}
        />
      )}
      {dealId && <BOVWriteupPanel dealId={dealId} />}
    </div>
  );
}
